import { WrenchIcon, CloudOffIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

export type WidgetUnavailableReason = "api-down" | "maintenance";

// Hardcoded copy, and useSuspense: false, because this screen is the last thing
// standing when infrastructure fails. Translations come from i18n.baback.co, and the
// namespace suspends by default — an i18n CDN incident would otherwise suspend the
// outage screen itself and leave the shopper looking at a blank iframe.
const COPY = {
  "api-down": {
    title: ["common.unavailable.title", "Service temporarily unavailable"],
    description: [
      "common.unavailable.description",
      "We're unable to load your return right now. Please try again in a few minutes.",
    ],
  },
  "maintenance": {
    title: ["common.maintenance.title", "Returns are temporarily paused"],
    description: [
      "common.maintenance.description",
      "We're carrying out maintenance on our return service. Please come back a little later.",
    ],
  },
} as const;

// Depends on neither the shop nor the API: it has to render when /public/shops is
// exactly what failed. Hence raw useTranslation rather than useCustomText (which
// reads the shop), and root design tokens rather than CustomerUiProvider (which
// needs the shop's primary colour).
export function WidgetUnavailable({ reason }: { reason: WidgetUnavailableReason }) {
  const { t } = useTranslation("return-widget-v3", { useSuspense: false });

  const Icon = reason === "maintenance" ? WrenchIcon : CloudOffIcon;
  const copy = COPY[reason];

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
      <Icon className="text-muted-foreground size-10" />
      <div className="flex flex-col gap-1">
        <p className="font-sans text-sm font-medium">
          {t(copy.title[0], { defaultValue: copy.title[1] })}
        </p>
        <p className="font-content text-muted-foreground text-sm">
          {t(copy.description[0], { defaultValue: copy.description[1] })}
        </p>
      </div>
      <button
        onClick={() => window.location.reload()}
        className="font-content text-primary cursor-pointer text-sm underline underline-offset-4"
      >
        {t("common.retry", { defaultValue: "Try again" })}
      </button>
    </div>
  );
}
